import { useEffect, useMemo, useRef } from "react";
import { Provider } from "react-redux";
import { setupListeners } from "@reduxjs/toolkit/query";
import { persistStore } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import { getStore } from "@/store/store";
import { publicRoutes } from "@/constants";

interface MiddlewareProviderProps {
  children: React.ReactNode;
}

const MiddlewareProvider = ({ children }: MiddlewareProviderProps) => {
  // Read the initial pathname once at mount time. We intentionally do NOT
  // subscribe to location changes (useLocation) because:
  //  1. The store is only created once (useMemo with []).
  //  2. useLocation would re-render this provider — and therefore the entire
  //     React tree below it — on every route change, which is wasteful.
  const isPublicRoute = publicRoutes.some((route) => window.location.pathname.startsWith(route));

  const store = useMemo(() => {
    return getStore(isPublicRoute);
  }, []);

  const persistorRef = useRef(persistStore(store));
  const persistor = persistorRef.current;

  useEffect(() => {
    setupListeners(store.dispatch);
  }, []);

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        {children}
      </PersistGate>
    </Provider>
  );
};

export { MiddlewareProvider };
